import { useState, type ReactNode } from 'react'
import type { Lead, EtapaPipeline } from '@/lib/types'
import LeadCard from './LeadCard'
import {
  IconTarget, IconPhone, IconSearch, IconFileText, IconBarChart, IconCheckCircle,
} from '@/components/icons/Icons'

interface PipelineColumn {
  etapa: EtapaPipeline
  label: string
  color: string
  /* El ícono vivía arriba, en una fila de seis tarjetas que repetía los
     mismos seis números que ya dice cada columna. Las tarjetas se fueron y el
     ícono se quedó, al lado del nombre de su etapa: ahí dice qué es esa
     columna sin ocupar una franja de la pantalla. */
  icono: ReactNode
}

/* Las seis etapas se distinguen entre si; ninguna es "mejor" que la anterior
   por su color, asi que van en la escala categorica y no en el semaforo. */
const COLUMNS: PipelineColumn[] = [
  { etapa: 'lead',        label: 'Lead',        color: 'var(--cat-4)', icono: <IconTarget size={14} /> },
  { etapa: 'contactado',  label: 'Contactado',  color: 'var(--cat-1)', icono: <IconPhone size={14} /> },
  { etapa: 'diagnostico', label: 'Diagnóstico', color: 'var(--cat-3)', icono: <IconSearch size={14} /> },
  { etapa: 'cotizado',    label: 'Cotizado',    color: 'var(--cat-5)', icono: <IconFileText size={14} /> },
  { etapa: 'negociacion', label: 'Negociación', color: 'var(--cat-7)', icono: <IconBarChart size={14} /> },
  { etapa: 'cerrado',     label: 'Cerrado',     color: 'var(--cat-2)', icono: <IconCheckCircle size={14} /> },
]

interface PipelineBoardProps {
  leads: Lead[]
  onStageChange: (leadId: number, newStage: EtapaPipeline) => void
  onLeadClick: (lead: Lead) => void
  selectedIds: Set<number>
  onToggleSelect: (id: number) => void
}

export default function PipelineBoard({ leads, onStageChange, onLeadClick, selectedIds, onToggleSelect }: PipelineBoardProps) {
  const [dragOverCol, setDragOverCol] = useState<EtapaPipeline | null>(null)

  function getDaysInStage(lead: Lead): number {
    if (!lead.fecha_primer_contacto) return 0
    return Math.max(0, Math.floor((Date.now() - new Date(lead.fecha_primer_contacto).getTime()) / 86400000))
  }

  function handleDragOver(e: React.DragEvent, etapa: EtapaPipeline) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverCol(etapa)
  }

  function handleDrop(e: React.DragEvent, nuevoEtapa: EtapaPipeline) {
    e.preventDefault()
    setDragOverCol(null)
    const leadId = Number(e.dataTransfer.getData('text/plain'))
    const lead = leads.find(l => l.id === leadId)
    if (!lead || lead.etapa === nuevoEtapa) return
    onStageChange(leadId, nuevoEtapa)
  }

  return (
    <div className="crm-pipeline-board">
      {COLUMNS.map(col => {
        const colLeads = leads.filter(l => l.etapa === col.etapa)
        return (
          <div
            key={col.etapa}
            className={`crm-pipeline-column${dragOverCol === col.etapa ? ' drag-over' : ''}`}
            onDragOver={e => handleDragOver(e, col.etapa)}
            onDragLeave={() => setDragOverCol(null)}
            onDrop={e => handleDrop(e, col.etapa)}
          >
            <div className="crm-pipeline-column-header">
              <div className="crm-pipeline-column-bar" style={{ background: col.color }} />
              <div className="crm-pipeline-column-header__inner">
                {/* Ícono y nombre son UNA cosa y van juntos a la izquierda: con
                    los tres sueltos, el `space-between` dejaba el nombre
                    flotando en el medio de la cabecera. */}
                <span className="crm-pipeline-column-etiqueta">
                  <span className="crm-pipeline-column-icono" style={{ color: col.color }}>{col.icono}</span>
                  <span className="crm-pipeline-column-title">{col.label}</span>
                </span>
                <span className="crm-pipeline-column-count" style={{ color: col.color }}>{colLeads.length}</span>
              </div>
            </div>
            <div className="crm-pipeline-column-body">
              {colLeads.map(lead => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  daysInStage={getDaysInStage(lead)}
                  onClick={() => onLeadClick(lead)}
                  borderColor={col.color}
                  isSelected={selectedIds.has(lead.id)}
                  onToggleSelect={onToggleSelect}
                />
              ))}
              {colLeads.length === 0 && <div className="kanban-empty"><span>Sin leads</span></div>}
            </div>
          </div>
        )
      })}
    </div>
  )
}
